// One finding, in three layers.
//
//   1  the claim, its context, where it is, how it was established, and the
//      decision — always visible, never scrolled past
//   2  "Show the check" — the quote and the working
//   3  "Where this comes from" — the audit cases and every internal identifier
//
// The order is the point: a reviewer meets the claim in their own language
// before they meet a failure-mode code. IA.md sets the layers; BRIEF.md's
// workbench test sets the bar for layer 1 — read a finding, judge it, decide on
// it, without opening a second disclosure.

import type { ReactNode } from 'react';

import FindingClaim from './FindingClaim';
import CheckReceipt from './CheckReceipt';
import FindingProvenance from './FindingProvenance';
import DispositionControl from './DispositionControl';
import type { Disposition, Flag } from '../../types';
import './FindingCard.css';

/**
 * Below this many findings, the selected finding opens with its check already
 * showing: on a short list there is room for it, and the reviewer is almost
 * certainly about to open it anyway. On a long list everything stays collapsed,
 * because a page of expanded receipts is not a list.
 */
export const SHOW_CHECK_BELOW = 4;

export function checkOpenByDefault(
  findingCount: number,
  selected: boolean,
): boolean {
  return selected && findingCount < SHOW_CHECK_BELOW;
}

export interface FindingCardProps {
  finding: Flag;
  disposition: Disposition | null;
  onDispositionChange(next: Disposition | null): void;
  /** The comment on the sheet: the reviewer's edit, or the generated draft. */
  comment?: string;
  onCommentChange?(next: string | null): void;
  commentEdited?: boolean;
  /** The finding the workspace is showing. Governs layer 2's default state. */
  selected?: boolean;
  /** How many findings are in the list, for that same default. */
  findingCount?: number;
  /**
   * Opens this finding's page in the document pane and takes the reviewer to
   * the quoted span. Passed through to the location chip, which becomes the
   * control that does it. Absent wherever there is no pane to open.
   */
  onShowInDocument?(): void;
  /**
   * One further confirmation this finding is waiting on, rendered between the
   * claim and the layers. The ask belongs inside the finding it would narrow —
   * a reviewer should never be asked for work before they have been given a
   * reason to care about the answer. Absent on every finding that needs none.
   */
  answer?: ReactNode;
}

export default function FindingCard({
  finding,
  disposition,
  onDispositionChange,
  comment,
  onCommentChange,
  commentEdited = false,
  selected = false,
  findingCount = 0,
  onShowInDocument,
  answer,
}: FindingCardProps) {
  const decided = disposition !== null;
  const state = decided ? disposition.decision : 'open';

  return (
    <article
      id={finding.id}
      className={`fc fc--${state}`}
      aria-labelledby={`${finding.id}-title`}
    >
      <FindingClaim
        finding={finding}
        titleId={`${finding.id}-title`}
        onShowInDocument={onShowInDocument}
      />

      {/* Still layer 1, and still above both disclosures — but a sibling of
          the claim rather than a child of it, because below 1024px it is
          sticky to the bottom of the viewport and a sticky element is bounded
          by its own containing block. Inside the claim it would come unstuck
          the moment the claim scrolled past, which is exactly when a reviewer
          reading the check still needs it. The gap back to the claim is the
          within-a-layer gap; see FindingCard.css. */}
      <DispositionControl
        findingId={finding.id}
        disposition={disposition}
        onDispositionChange={onDispositionChange}
        comment={comment ?? finding.commentDraft}
        onCommentChange={onCommentChange ?? (() => undefined)}
        commentEdited={commentEdited}
      />

      {answer}

      <div className="fc__layers">
        <CheckReceipt
          finding={finding}
          defaultOpen={checkOpenByDefault(findingCount, selected)}
        />
        <FindingProvenance finding={finding} />
      </div>
    </article>
  );
}
